import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../../../helpers/download';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, Popover, pivotData} from 'wc-lib';
import {format} from 'date-fns';

require('wc-lib/icons/electricity.svg');
require('wc-lib/icons/table.svg');
require('wc-lib/icons/download.svg');
require('wc-lib/icons/graph.svg');
require('wc-lib/icons/infocircle.svg');

@Component({
  template: require('./estimated-unserved-energy.html'),
  name: 'estimated-unserved-energy',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class EstimatedUnservedEnergy extends Vue {
  data: Array<any> = [];
  dynamicView: CustomDynamicView<any>;
  pivotedData: Array<any> = [];
  chartVisible = true;
  tableVisible = false;
  availablePoeTypes: string[] = [];
  selectedPoeType: string = '';
  availablePeriods: string[] = [];
  runDateTime: string = "";
  runNo: string = "";
  id: string;
  columns: Array<any> = [
    { title: 'Region', field: 'REGIONID' }, 
    { title: 'Year', field: 'YEAR' },
    { title: 'Unserved Energy (%)', field: 'USE_WEIGHTED_AVG' } 
  ];
  infoText: string = `This figure shows annual unserved energy as a percentage of total energy over a rolling one-year window for each region. The results are shown (alongside the reliability standard) as the weighted average across simulations using 10% and 50% POE peak demand forecasts.

Probability of Exceedance (POE) means the probability (as a percentage) that a maximum demand forecast will be met or exceeded (for example, due to weather conditions). For example, a 10% POE forecast is expected to be met or exceeded, on average, only one out of every 10 years, so considers more extreme weather than a 50% POE forecast, which is expected to be met or exceeded, on average, one out of every two years.`;

  get chartButtonType(): string {
    return this.chartVisible ? 'primary' : 'ghost';
  }

  get tableButtonType(): string {
    return this.tableVisible ? 'primary' : 'ghost';
  }

  chartConfig: any = {
    type: 'serial',
    theme: 'simpleblue',
    valueAxes: [{
        id: 'v1',
        position: 'left',
        title: 'Unserved Energy (%)',
        labelsEnabled: true,
        minimum: 0
      }],
    graphs: [
      { valueAxis: 'v1', balloonText: '[[title]] : [[value]] %', title: 'Reliability Standard', lineThickness:3, valueField: 'ReliabilityStandard' }
    ],
    chartCursor: {
      categoryBalloonEnabled: false,
      cursorAlpha: 0,
      zoomable: false
    },
    categoryAxis: {
      parseDates: false,
      //labelRotation: 45,
      color: '#6F7072',
      gridAlpha: 0.5,
      autoGridCount: true
    },
    legend: {
      equalWidths: true,
      useGraphSettings: false,
      position: 'top',
      valueAlign: 'left'
    },
    categoryField: 'REGIONID'
  }

  applyFilter() {
    this.dynamicView.removeFilters();
    this.dynamicView.applyFind({ 'DEMAND_POE_TYPE': this.selectedPoeType });
    this.data = this.dynamicView.data();
    this.processData();
  }

  processData() {
    let data = this.data;
    this.pivotedData = pivotData(data, ['REGIONID'], 'YEAR', 'USE_WEIGHTED_AVG');
    for (let row of this.pivotedData) {
      row[`ReliabilityStandard`] = 0.002;
    }
  }

  async created() {
    this.id = await DataManager.addEndPoint('../../api/report/MTPASA_CHART_2a', undefined, undefined, 'MTPASA_CHART_2a', undefined, 'yyyy-MM-ddTHH:mm:ss', ['RUN_DATETIME', 'PERIOD_ENDING']);
    this.dynamicView = DataManager.getDynamicView(this.id, 'distributed');
    this.dynamicView.removeFilters();
    let allData = this.dynamicView.data();
    this.runDateTime = format(allData[0].RUN_DATETIME, 'yyyy-MM-dd HH:mm:ss');
    this.runNo = allData[0].RUN_NO;
    allData.forEach( x => {
      x.YEAR = format(x.PERIOD_ENDING, 'yyyy');
      if(this.availablePoeTypes.indexOf(x.DEMAND_POE_TYPE) === -1) {
        this.availablePoeTypes.push(x.DEMAND_POE_TYPE);
      }
      if(this.availablePeriods.indexOf(x.YEAR) === -1) {
        this.availablePeriods.push(x.YEAR);
      }
      this.selectedPoeType = x.DEMAND_POE_TYPE;
    });

    for(let period of this.availablePeriods) {
      this.chartConfig.graphs.push({ valueAxis: 'v1', type: 'column', balloonText: '[[title]] : [[value]] %', title: `${period}`, lineAlpha: 0.2, fillAlphas: 0.8, valueField: `${period} USE_WEIGHTED_AVG` });
    }
    this.applyFilter();
  }

  @Watch('selectedPoeType')
  onSelectedPoeTypeChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  chartClick() {
    this.chartVisible = true;
    this.tableVisible = false;
  }

  tableClick() {
    this.tableVisible = true;
    this.chartVisible = false;
  }

  downloadClick() {
    DownloadHelper.download('MTPASA_CHART_1', this.data, this.columns);
  }
}



// WEBPACK FOOTER //
// src/views/electricity/nem/mtpasa/estimated-unserved-energy.ts